import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAction, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { getCurrentUserId } from "@/lib/auth";

declare global {
  interface Window {
    snap?: {
      pay: (token: string, options?: {
        onSuccess?: (result: unknown) => void;
        onPending?: (result: unknown) => void;
        onError?: (result: unknown) => void;
        onClose?: () => void;
      }) => void;
    };
  }
}

const SCRIPT_URL = "https://app.sandbox.midtrans.com/snap/snap.js";
const CLIENT_KEY = import.meta.env.VITE_MIDTRANS_CLIENT_KEY;
const IS_PRODUCTION = import.meta.env.VITE_MIDTRANS_IS_PRODUCTION === "true";

export default function Checkout() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState<"pro" | "enterprise">("pro");
  const [loading, setLoading] = useState(false);
  const [snapReady, setSnapReady] = useState(false);

  const hostId = getCurrentUserId();
  const event = useQuery(api.events.getById, eventId ? { id: eventId as Id<"events"> } : "skip");
  const settings = useQuery(api.admin.getPlatformSettings);
  const createSnap = useAction(api.midtransActions.createSnapTransaction);

  const dynamicPackages = [
    { tier: "free" as const, name: "Free", price: 0, features: ["50 tamu", "200 foto", "Face match dasar", "Notifikasi WA manual"], cta: "Paket Default" },
    { tier: "pro" as const, name: "Pro", price: settings?.pricing?.pro ?? 99000, features: ["500 tamu", "2000 foto", "Face match prioritas", "Notifikasi WA otomatis", "Hapus watermark"], cta: "Upgrade ke Pro" },
    { tier: "enterprise" as const, name: "Enterprise", price: settings?.pricing?.enterprise ?? 499000, features: ["5000 tamu", "50000 foto", "Face match prioritas + review", "Notifikasi WA otomatis", "Dedicated support", "Custom branding"], cta: "Upgrade ke Enterprise" },
  ];

  // Load Midtrans Snap.js script (lazy, sekali aja)
  useEffect(() => {
    if (!CLIENT_KEY) {
      console.error("VITE_MIDTRANS_CLIENT_KEY belum diset — Snap popup akan gagal.");
    }
    if (document.querySelector(`script[src="${SCRIPT_URL}"]`)) {
      setSnapReady(true);
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.setAttribute("data-client-key", CLIENT_KEY || "");
    script.onload = () => setSnapReady(true);
    script.onerror = () => console.error("Gagal load Midtrans Snap.js");
    document.head.appendChild(script);
  }, []);

  // Redirect jika belum login
  if (!hostId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full p-6 text-center space-y-3">
          <h2 className="font-bold text-slate-900">Login dulu</h2>
          <p className="text-sm text-slate-500">Silakan login sebagai host untuk melanjutkan pembayaran.</p>
          <Button onClick={() => navigate("/login")} className="w-full">Login</Button>
        </Card>
      </div>
    );
  }

  // Verifikasi event ada & host valid
  if (event === undefined) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }
  if (event === null) {
    return <div className="min-h-screen flex items-center justify-center p-6 text-slate-500">Event tidak ditemukan.</div>;
  }
  if (event.hostId !== hostId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-md w-full p-6 text-center space-y-3">
          <h2 className="font-bold text-red-600">Akses Ditolak</h2>
          <p className="text-sm text-slate-500">Anda bukan host dari event ini.</p>
          <Button onClick={() => navigate("/dashboard")} variant="outline" className="w-full">Kembali</Button>
        </Card>
      </div>
    );
  }

  const handleCheckout = async () => {
    if (!eventId || !snapReady || !window.snap || loading) return;
    setLoading(true);
    try {
      const result = await createSnap({
        eventId: eventId as Id<"events">,
        tier: selectedTier,
      });

      const eventSlug = event?.slug;

      window.snap.pay(result.snapToken, {
        onSuccess: () => {
          setLoading(false);
          alert("Pembayaran berhasil! Event Anda telah di-upgrade.");
          navigate(eventSlug ? `/dashboard/${eventSlug}` : "/dashboard");
        },
        onPending: () => {
          setLoading(false);
          alert("Pembayaran pending. Selesaikan pembayaran untuk mengaktifkan paket.");
          navigate(eventSlug ? `/dashboard/${eventSlug}` : "/dashboard");
        },
        onError: () => {
          setLoading(false);
          alert("Pembayaran gagal. Silakan coba lagi.");
        },
        onClose: () => {
          setLoading(false);
        },
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : "Gagal membuat transaksi");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="px-6 py-4 flex items-center gap-3 border-b bg-white">
        <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <span className="font-bold text-lg text-slate-900">Upgrade Paket Event</span>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-extrabold text-slate-900">Pilih Paket yang Sesuai</h1>
          <p className="text-sm text-slate-500 mt-1">Bayar per event via Midtrans (QRIS, VA, E-Wallet)</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {dynamicPackages.map((pkg) => (
            <Card key={pkg.tier} className={`relative ${selectedTier === pkg.tier ? "ring-2 ring-blue-600" : ""}`}>
              {pkg.tier === "pro" && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-blue-600 text-white text-xs font-bold rounded-full">
                  POPULER
                </div>
              )}
              <CardHeader>
                <CardTitle className="text-lg">{pkg.name}</CardTitle>
                <CardDescription>
                  {pkg.price === 0 ? "Gratis" : `Rp ${pkg.price.toLocaleString("id-ID")}/event`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {pkg.features.map((f) => (
                  <div key={f} className="flex items-center gap-2 text-xs text-slate-600">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </CardContent>
              <CardFooter>
                {pkg.tier === "free" ? (
                  <Button variant="outline" className="w-full text-xs" disabled>
                    {event.tier === "free" ? "Paket Saat Ini" : "Default"}
                  </Button>
                ) : (
                  <Button
                    className="w-full text-xs"
                    variant={selectedTier === pkg.tier ? "default" : "outline"}
                    onClick={() => setSelectedTier(pkg.tier)}
                  >
                    {selectedTier === pkg.tier ? "Dipilih ✓" : `Pilih ${pkg.name}`}
                  </Button>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="mt-8 max-w-md mx-auto">
          <Button
            className="w-full"
            disabled={loading || !snapReady}
            onClick={handleCheckout}
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memproses...</>
            ) : !snapReady ? (
              "Memuat pembayaran..."
            ) : (
              `Bayar Rp ${dynamicPackages.find((p) => p.tier === selectedTier)?.price.toLocaleString("id-ID")} via Midtrans`
            )}
          </Button>
          <p className="text-center text-xs text-slate-400 mt-2">
            Pembayaran aman via Midtrans Snap ({IS_PRODUCTION ? "Production" : "Sandbox"})
          </p>
        </div>
      </main>
    </div>
  );
}